/*
 *
 *  * Copyright (c) Funtip GbR 2018.
 *  * Simon Oldeboershuis, Perry Prust
 *
 */

import { all, put, takeEvery } from 'redux-saga/effects';
import { Action } from 'typescript-fsa';
import { Job } from '../../services/playlist';
import { fetchWrapperFunc } from '../../utils';
import { getJobApi, ServerType } from '../System/systemConfig';
import {
    CreateJobActionParams,
    CREATE_JOB_ACTION,
    DELETE_SCHEDULED_JOB,
    DISABLE_JOB_SCHEDULER,
    ENABLE_JOB_SCHEDULER,
    jobManagementSetServerAvailable,
    loadJobsAction,
    LOAD_JOBS,
    setAvailableJobs,
    setJobSchedulerState,
    setScheduledJobs,
    setShowCreateJobFormAction
} from './actions';

function* loadJobsGenerator(action: Action<ServerType>) {
    const serverName = action.payload;

    if (!serverName) {
        console.info('no server url found for job server ' + serverName);
    } else {
        const jobApi = getJobApi(serverName);
        yield* fetchWrapperFunc(
            () => jobApi.getScheduledJobs(),
            [
                (r: Job[]) => setScheduledJobs({ serverName: serverName, jobs: r }),
                (r: Job[]) => jobManagementSetServerAvailable({ serverName: serverName, available: true })
            ],
            () => jobManagementSetServerAvailable({ serverName: serverName, available: false })
        );

        yield* fetchWrapperFunc(() => jobApi.getAvailableJobs(), [
            (r: string[]) => setAvailableJobs({ serverName: serverName, jobNames: r })
        ]);

        yield* fetchWrapperFunc(() => jobApi.isSchedulerEnabled(), [
            (r: boolean) => setJobSchedulerState({ serverName: serverName, enabled: r })
        ]);
    }
}

function* enableJobSchedulerGenerator(action: Action<ServerType>) {
    const serverName = action.payload;

    if (!serverName) {
        console.info('no server url found for job server ' + serverName);
    } else {
        const jobApi = getJobApi(serverName);
        yield* fetchWrapperFunc(() => jobApi.start());
        yield put(loadJobsAction(serverName));
    }
}

function* disableJobSchedulerGenerator(action: Action<ServerType>) {
    const serverName = action.payload;

    if (!serverName) {
        console.info('no server url found for job server ' + serverName);
    } else {
        const jobApi = getJobApi(serverName);
        yield* fetchWrapperFunc(() => jobApi.standby());

        yield put(loadJobsAction(serverName));
    }
}

function* deleteScheduledJobGenerator(
    action: Action<{ serverName: ServerType; triggerId: string; triggerGroup: string }>
) {
    const serverName = action.payload.serverName;
    const triggerId = action.payload.triggerId;
    const triggerGroup = action.payload.triggerGroup;

    if (!serverName) {
        console.info('no server url found for job server ' + serverName);
    } else {
        const jobApi = getJobApi(serverName);
        yield* fetchWrapperFunc(() => jobApi._delete(triggerId, triggerGroup));

        // reload the scheduled jobs
        yield put(loadJobsAction(serverName));
    }
}

function* createJobGenerator(action: Action<CreateJobActionParams>) {
    const serverName = action.payload.serverName;

    if (!serverName) {
        console.info('no server url found for job server ' + serverName);
    } else {
        const jobApi = getJobApi(serverName);

        yield* fetchWrapperFunc(() =>
            jobApi.create(
                action.payload.jobId,
                action.payload.immediately,
                action.payload.seconds,
                action.payload.minute,
                action.payload.hours,
                action.payload.dayOfMonth,
                action.payload.months,
                action.payload.daysOfWeek,
                action.payload.years
            )
        );

        // reload the scheduled jobs
        yield put(loadJobsAction(serverName));
        yield put(setShowCreateJobFormAction({ serverName, show: false }));
    }
}

function* watchForJobsGenerator() {
    yield takeEvery(LOAD_JOBS, loadJobsGenerator);
    yield takeEvery(DELETE_SCHEDULED_JOB, deleteScheduledJobGenerator);
    yield takeEvery(ENABLE_JOB_SCHEDULER, enableJobSchedulerGenerator);
    yield takeEvery(DISABLE_JOB_SCHEDULER, disableJobSchedulerGenerator);
    yield takeEvery(CREATE_JOB_ACTION, createJobGenerator);
}

export function* jobManagementSagas() {
    yield all([watchForJobsGenerator()]);
}
